import React from 'react';
import { Box } from '@tanium/react-box';
import { useSelection } from '@tanium/react-use-selection';
import { LayoutStyleProps } from '@tanium/style-system';
import { RibbonListProvider } from '../../context/RibbonListProvider';
import { useColumnSort } from '../../hooks/useColumnSort';
import { useExpansion } from '../../hooks/useExpansion';
import { Id } from '../../types/Id';
import { RibbonListProps } from '../../types/StackedRibbonListComponentTypes';
import { validateColumns } from '../../utils/utils';
import { HeaderProps, RibbonListHeader } from '../ListHeader/RibbonListHeader';

export interface RibbonListWrapperProps<TId extends Id, TItem>
  extends RibbonListProps<TId, TItem>,
    Pick<HeaderProps<TId, TItem>, 'isListOverflowing' | 'onSortChange' | 'sort' | 'totalItemCount'>,
    Pick<LayoutStyleProps, 'height'> {
  /**
   * The contents of the list; should be some kind of RibbonList component.
   */
  children: React.ReactNode;
}

/**
 * A wrapper component to be used around a RibbonList component, providing the RibbonListHeader,
 * context provider, and list container.
 */
const RibbonListWrapperInner = <TId extends Id, TItem>({
  actions,
  actionsVerticalAlignment,
  children,
  checkboxVerticalAlignment,
  columns,
  dataGetter,
  disclosureVerticalAlignment,
  enableRowHighlight,
  height = '100%',
  isIdEmphasized,
  isItemLoaded,
  isListOverflowing,
  isLoading,
  isSelectionEnabled,
  itemCount,
  itemIdGetter,
  onSortChange,
  selectionTooltipContent,
  sort: sortProp,
  totalItemCount,
  verticalAlignment,
  // selection props
  enableSelectAll,
  isSelectable: isSelectableProp,
  selection: selectionProp,
  selectionMode: selectionModeProp,
  onSelectionChange: onSelectionChangeProp,
  // expansion props
  expandableContent,
  defaultExpansion: defaultExpansionProp,
  defaultExpansionMode: defaultExpansionModeProp,
  expansion: expansionProp,
  expansionMode: expansionModeProp,
  onExpansionChange: onExpansionChangeProp,
  hideExpansionDisclosure: hideExpansionDisclosureProp,
  enableExpandAll: enableExpandAllProp,
}: RibbonListWrapperProps<TId, TItem>) => {
  validateColumns(columns);
  const {
    clearSelection,
    isIdSelected,
    isSelectable,
    selectAll,
    selection,
    selectionMode,
    toggleSelection,
  } = useSelection<TId>({
    isSelectable: isSelectableProp,
    onSelectionChange: onSelectionChangeProp,
    selection: selectionProp,
    selectionMode: selectionModeProp,
  });

  // The user may or may not have provided expansion-related props to StackedRibbonList.
  // Regardless, pass these through to the useExpansion hook and it will generate fresh
  // props for subcomponents to use.
  const {
    expansionControlProps: {
      expansion,
      expansionMode,
      isExpandable,
      hideExpansionDisclosure,
      enableExpandAll,
    },
    clearExpansion,
    expandAll,
    isIdExpanded,
    toggleExpansion,
  } = useExpansion<TId>({
    defaultExpansion: defaultExpansionProp,
    defaultExpansionMode: defaultExpansionModeProp,
    expansion: expansionProp,
    expansionMode: expansionModeProp,
    isExpandable: !!expandableContent,
    onExpansionChange: onExpansionChangeProp,
    hideExpansionDisclosure: hideExpansionDisclosureProp,
    enableExpandAll: enableExpandAllProp,
  });

  const { getColumnSortDirection, sort, toggleSort } = useColumnSort({
    onSortChange,
    sort: sortProp,
  });

  return (
    <Box width="100%" height={height} role="grid">
      <RibbonListHeader
        columns={columns}
        getColumnSortDirection={getColumnSortDirection}
        isListOverflowing={isListOverflowing}
        isSelectionEnabled={isSelectionEnabled}
        itemCount={itemCount}
        selectionTooltipContent={selectionTooltipContent}
        sort={sort}
        toggleSort={toggleSort}
        totalItemCount={totalItemCount}
        // selection props
        isSelectable={isSelectable}
        selectAll={selectAll}
        selection={selection}
        selectionMode={selectionMode}
        clearSelection={clearSelection}
        // expansion props
        isExpandable={isExpandable}
        expansion={expansion}
        expansionMode={expansionMode}
        expandAll={expandAll}
        clearExpansion={clearExpansion}
        hideExpansionDisclosure={hideExpansionDisclosure}
        enableExpandAll={enableExpandAll}
        enableSelectAll={enableSelectAll}
      />
      <RibbonListProvider
        actions={actions}
        actionsVerticalAlignment={actionsVerticalAlignment}
        checkboxVerticalAlignment={checkboxVerticalAlignment}
        columns={columns}
        dataGetter={dataGetter}
        disclosureVerticalAlignment={disclosureVerticalAlignment}
        enableRowHighlight={enableRowHighlight}
        isIdEmphasized={isIdEmphasized}
        isItemLoaded={isItemLoaded}
        isLoading={isLoading}
        isSelectionEnabled={isSelectionEnabled}
        itemIdGetter={itemIdGetter}
        selectionTooltipContent={selectionTooltipContent}
        verticalAlignment={verticalAlignment}
        // selection props
        isSelectable={isSelectable}
        isIdSelected={isIdSelected}
        toggleSelection={toggleSelection}
        // expansion props
        expandableContent={expandableContent}
        isExpandable={isExpandable}
        isIdExpanded={isIdExpanded}
        toggleExpansion={toggleExpansion}
        hideExpansionDisclosure={hideExpansionDisclosure}
      >
        {children}
      </RibbonListProvider>
    </Box>
  );
};

export const RibbonListWrapper = React.memo(RibbonListWrapperInner) as <TId extends Id, TItem>(
  props: RibbonListWrapperProps<TId, TItem>,
) => JSX.Element;
